import { animated, useTransition } from "@react-spring/web"
import ImagePlaceholder from "assets/icons/imagePlaceholder.svg"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useIsMountedRef } from "shared/hooks/useIsMounted"
import { buttonFocus } from "shared/styles/mixins"
import styled, { css } from "styled-components"

import { Price } from "../ui/priceComponents/Price"
import { ProductTitle } from "../ui/textStyles"

const IMAGE_HEIGHT_PX = 80
const BANNER_WIDTH_PX = 145

const APPEAR_ANIMATION_DURATION_MS = 200
const EXPAND_ANIMATION_DURATION_MS = 500
const HOVER_ANIMATION_DURATION_MS = 300
const EXPANDED_DURATION_MS = 4000 // duration for the card to stay expanded after appearing

export const expandWidthTransition = css`width ${EXPAND_ANIMATION_DURATION_MS}ms`
export const hoverTransition = css`transform ${HOVER_ANIMATION_DURATION_MS}ms`

const Button = styled(animated.button)<{
  $expanded: boolean
}>`
  border: none;
  appearance: none;
  padding: 0;

  display: flex;
  flex-direction: row;
  align-items: stretch;

  width: ${IMAGE_HEIGHT_PX + BANNER_WIDTH_PX}px;
  ${(props) =>
    !props.$expanded &&
    `
    width: ${IMAGE_HEIGHT_PX}px;
  `}

  transition: ${expandWidthTransition}, ${hoverTransition};

  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.4);
  overflow: hidden;

  ${buttonFocus}

  &:hover {
    transform: scale(1.03);
  }
  transform-origin: center left;
`

const ImageWrapper = styled.div<{ $src?: string }>`
  width: ${IMAGE_HEIGHT_PX}px;
  height: ${IMAGE_HEIGHT_PX}px;
  overflow: hidden;
  flex-shrink: 0;

  background-image: ${(props) => `url(${props.$src})`};
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  svg {
    margin: 0 auto;
    width: 100%;
    height: 100%;
  }
`

const InfoWrapper = styled.div`
  padding: 7px 9px;
  box-sizing: border-box;
  width: ${BANNER_WIDTH_PX}px;
  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Title = styled(ProductTitle)`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.3;
  margin: 0;
`

const PriceWrapper = styled.div`
  margin-top: auto;
`

export type FeaturedProduct = {
  id: string
  title: string
  priceAmount: string | number
  currencyCode: string
  displayCurrencyCode?: boolean
  imageSrc?: string
}

export type FeaturedProductButtonProps = {
  product?: FeaturedProduct
  // ^ parent can pass no props: this means there is no product to show.
  // we do this instead of just not rendering this component, because we want the disappear animation to show
  onClick?: () => void
}

export const FeaturedProductButton = ({
  product,
  onClick,
}: FeaturedProductButtonProps) => {
  const shouldDisplayProduct = !!product

  const [expanded, setExpanded] = useState(false)
  const isHoveringRef = useRef(false)

  const isMountedRef = useIsMountedRef()
  const animationTimeoutRef = useRef<number | null>(null) // for one product's animation

  // Animation helper for this component - starts a timeout which checks isMounted before doing anything
  const setAnimationTimeout = useCallback(
    (duration: number, callback: (...args: any) => void) => {
      animationTimeoutRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) {
          return
        }
        callback()
      }, duration)
    },
    [],
  )

  // Appear/disappear animation effects, upon any change to the featured product
  useEffect(() => {
    setExpanded(false)
    window.clearTimeout(animationTimeoutRef.current)
    // Appearing animation sequence - expand after fade-in is finished, then collapse after some time
    if (shouldDisplayProduct) {
      // Step 1: Fading in. This sets a timeout to expand
      setAnimationTimeout(APPEAR_ANIMATION_DURATION_MS + 200, () => {
        setExpanded(true)
        // Step 2: Expanding. This sets a timeout to collapse (if not hovering)
        setAnimationTimeout(EXPANDED_DURATION_MS, () => {
          if (!isHoveringRef.current) {
            setExpanded(false)
          }
        })
      })
    }
    // Disappearing animation - immediately collapse
    if (!shouldDisplayProduct) {
      setExpanded(false)
    }
  }, [product?.id])

  // Expand on hover
  const onHover = useCallback(() => {
    isHoveringRef.current = true
    window.clearTimeout(animationTimeoutRef.current) // don't collapse during hover
    if (shouldDisplayProduct) {
      setExpanded(true)
    }
  }, [shouldDisplayProduct])

  // After hovering ends, collapse after some time
  const onLeave = useCallback(() => {
    isHoveringRef.current = false
    setAnimationTimeout(EXPANDED_DURATION_MS, () => {
      setExpanded(false)
    })
  }, [])

  // Everything before this point just determines `product` and `expanded`. react-spring handles the rest.

  // We need to memoize the product because otherwise useTransition considers the product a new product each render
  // I tried using spring's `keys` parameter to fix this instead,
  // but then the component broke if you toggle on/off quickly.
  const animatedProduct = useMemo(() => product, [product?.id])

  // This react-spring animation only controls opacity.
  // react-spring's useTransition makes disappear animations possible.
  const transitions = useTransition(animatedProduct, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: {
      duration: APPEAR_ANIMATION_DURATION_MS,
    },
    exitBeforeEnter: true,
  })

  return transitions((style, item) => {
    if (!item) {
      return null
    }

    return (
      <Button
        $expanded={expanded}
        onClick={onClick}
        style={style}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        data-robot="featured-product"
      >
        <ImageWrapper $src={item.imageSrc}>
          {!item.imageSrc && <ImagePlaceholder />}
        </ImageWrapper>
        <InfoWrapper>
          <Title $maxLines={2}>{item.title}</Title>
          <PriceWrapper>
            <Price
              size="sm"
              amount={item.priceAmount}
              showCurrencyCode={item.displayCurrencyCode}
              currencyCode={item.currencyCode}
            />
          </PriceWrapper>
        </InfoWrapper>
      </Button>
    )
  })
}
